import * as PropTypes from "prop-types";
import classNames from "classnames";
import { Marker } from "react-map-gl";
import markerClusterStyles from "./MarkerCluster.css";

export class MarkerCluster extends React.Component {
  static propTypes = {
    markers: PropTypes.array.isRequired,
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
    onMarkerClick: PropTypes.func.isRequired,
    activeMarkerIndex: PropTypes.number,
    zoom: PropTypes.number,
    onZoomToCluster: PropTypes.func,
  };

  state = {
    isFannedOut: false,
    isAnimating: false,
  };

  handleClusterClick = () => {
    const { zoom, latitude, longitude, onZoomToCluster } = this.props;
    if (zoom && zoom < 5.5 && onZoomToCluster) {
      if( zoom == 2.4 ) {
        onZoomToCluster(latitude, longitude, 4.5);
      } else {
        onZoomToCluster(latitude, longitude, 5.5);
      }
    } else {
      if (!this.state.isFannedOut) {
        this.setState({ isFannedOut: true, isAnimating: true });
        setTimeout(() => {
          this.setState({ isAnimating: false });
        }, 10);
      } else {
        this.setState({ isFannedOut: false, isAnimating: false });
      }
    }
  };

  getFannedOutPosition(index, total) {
    const radius = 60;
    const angleStep = (2 * Math.PI) / total;
    const angle = angleStep * index - Math.PI / 2;
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  }

  render() {
    const { markers, latitude, longitude, activeMarkerIndex } = this.props;
    const { isFannedOut, isAnimating } = this.state;
    const size = 37;
    const offset = -1 * (size / 2);

    if (markers.length === 1) {
      return (
        <Marker
          offsetLeft={offset}
          offsetTop={offset}
          longitude={longitude}
          latitude={latitude}
        >
          <div style={{ position: 'relative', zIndex: 100 }}>
            <MarkerIcon
              size={size}
              onClick={() => this.props.onMarkerClick(markers[0].marker, markers[0].index)}
              isActive={activeMarkerIndex === markers[0].index}
            />
          </div>
        </Marker>
      );
    }

    return (
      <React.Fragment>
        {!isFannedOut ? (
          <Marker
            offsetLeft={offset}
            offsetTop={offset}
            longitude={longitude}
            latitude={latitude}
          >
            <div style={{ position: 'relative', zIndex: 100 }}>
              <ClusterIcon
                size={size}
                count={markers.length}
                onClick={this.handleClusterClick}
              />
            </div>
          </Marker>
        ) : (
          markers.map((markerData, idx) => {
            const position = this.getFannedOutPosition(idx, markers.length);
            return (
              <Marker
                key={`fanned-${markerData.index}`}
                offsetLeft={offset}
                offsetTop={offset}
                longitude={longitude}
                latitude={latitude}
              >
                <div
                  style={{
                    transform: isAnimating 
                      ? 'translate(0, 0) scale(0.5)' 
                      : `translate(${position.x}px, ${position.y}px) scale(1)`,
                    opacity: isAnimating ? 0 : 1,
                    position: 'relative',
                    zIndex: isFannedOut ? 1001 : 1,
                  }}
                  className={markerClusterStyles.fannedMarker}
                >
                  <MarkerIcon
                    size={size}
                    onClick={() => this.props.onMarkerClick(markerData.marker, markerData.index)}
                    isActive={activeMarkerIndex === markerData.index}
                  />
                </div>
              </Marker>
            );
          })
        )}
        {isFannedOut && (
          <Marker
            offsetLeft={offset}
            offsetTop={offset}
            longitude={longitude}
            latitude={latitude}
            style={{ position: 'relative', zIndex: 1002 }}
          >
            <div>
              <CloseClusterIcon
                size={size}
                onClick={this.handleClusterClick}
              />
            </div>
          </Marker>
        )}
      </React.Fragment>
    );
  }
}

const ClusterIcon = ({ size, count, onClick }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height={size}
      viewBox="0 0 37 37"
      onClick={onClick}
      className={markerClusterStyles.clusterIcon}
      style={{ cursor: 'pointer' }}
    >
      <g fill="none" fillRule="evenodd" transform="translate(-7 -7)">
        <circle
          cx="25.5"
          cy="25.5"
          r="17.5"
          fill="#00124a"
          stroke="#FFF"
          strokeWidth="2"
          transform="rotate(45 25.5 25.5)"
        />
        <text
          x="25.5"
          y="30"
          textAnchor="middle"
          fill="#FFF"
          fontSize="14"
          fontWeight="bold"
          fontFamily="Arial, sans-serif"
        >
          {count}
        </text>
      </g>
    </svg>
  );
};

const CloseClusterIcon = ({ size, onClick }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height={size}
      viewBox="0 0 37 37"
      onClick={onClick}
      className={markerClusterStyles.closeIcon}
      style={{ cursor: 'pointer' }}
    >
      <g fill="none" fillRule="evenodd" transform="translate(-7 -7)">
        <circle
          cx="25.5"
          cy="25.5"
          r="17.5"
          fill="#333"
          stroke="#FFF"
          strokeWidth="2"
        />
        <line
          x1="19"
          y1="19"
          x2="32"
          y2="32"
          stroke="#FFF"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="32"
          y1="19"
          x2="19"
          y2="32"
          stroke="#FFF"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
};

const MarkerIcon = ({ size, onClick, isActive }) => {

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height={size}
      viewBox="0 0 37 37"
      onClick={onClick}
      style={{ cursor: 'pointer', opacity: isActive ? 0.3 : 1 }}
    >
      <g fill="none" fillRule="evenodd" transform="translate(-7 -7)">
        <circle
          cx="25.5"
          cy="25.5"
          r="17.5"
          stroke="#FFF"
          strokeWidth="2"
          transform="rotate(45 25.5 25.5)"
        />
        <circle cx="25.5" cy="25.5" r="1.5" fill="#FFF" />
      </g>
    </svg>
  );
};

const MarkerIconStar = ({ size, onClick, isActive }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height={size}
      viewBox="0 0 163.38 155.39"
      onClick={onClick}
      style={{ cursor: 'pointer', opacity: isActive ? 0.3 : 1 }}
    >
      <g>
        <polygon
          points="81.69 0 100.98 59.35 163.38 59.35 112.9 96.03 132.18 155.39 81.69 118.71 31.2 155.39 50.49 96.03 0 59.35 62.41 59.35 81.69 0"
          fill="#c69355"
        />
      </g>
    </svg>
  );
};
